import { useMemo, useState } from "react";
import { Skeleton } from "@/components/ui/skeleton";
import {
  useAddStruktura,
  useDeleteStruktura,
  usePatchStruktura,
  useStruktury,
  useVyrobcovia,
  type StrukturaCreate,
  type StrukturaItem,
} from "@/api/hooks/useCennik";
import { toast } from "@/components/ui/use-toast";
import { extractErrorMessage } from "@/api/client";

type TypPovrchu = StrukturaCreate["typ_povrchu"];

const TYP_COLORS: Record<string, string> = {
  lesk: "bg-yellow-100 text-yellow-800",
  mat: "bg-blue-100 text-blue-800",
  semi_mat: "bg-purple-100 text-purple-800",
  por: "bg-green-100 text-green-800",
  synchro: "bg-orange-100 text-orange-800",
  special: "bg-gray-100 text-gray-800",
};

/** Typy, pri ktorých má nová štruktúra predvolený smer vlákien (ako server). */
const TYPY_SO_SMEROM = new Set<string>(["por", "synchro"]);

/** „Lesk — vysoký lesk, zrkadlový…" → „Lesk" (popis typu ide do tooltipu). */
function kratkyTyp(labels: Record<string, string>, typ: string): string {
  return (labels[typ] ?? typ).split(" — ")[0] ?? typ;
}

/**
 * Katalóg povrchových štruktúr (KOLO 166 vytiahnuté z `CennikPage`).
 *
 * Od KOLA 168 je katalóg v DB a identita štruktúry je (kód, výrobca) —
 * `SE` je u Kronospanu drevený pór, u Pfleiderera oceľ. Preto stĺpec
 * Výrobca, mazanie podľa `id` a prepínač **Smer vlákien** per riadok:
 * dosky so štruktúrou so smerom dostanú pri importe cenníka smer X.
 * Typy povrchu aj ich popisy idú zo servera (`typ_povrchu_labels`).
 */
export function StrukturyPanel(): JSX.Element {
  const query = useStruktury();
  const addMutation = useAddStruktura();
  const patchMutation = usePatchStruktura();
  const deleteMutation = useDeleteStruktura();
  const [filterVyrobca, setFilterVyrobca] = useState<string>("all");
  const [filterTyp, setFilterTyp] = useState<string>("all");
  const [searchS, setSearchS] = useState("");
  const [adding, setAdding] = useState(false);
  const [newKod, setNewKod] = useState("");
  const [newNazov, setNewNazov] = useState("");
  const [newVyrobca, setNewVyrobca] = useState("");
  const [newTyp, setNewTyp] = useState<TypPovrchu>("por");
  const [newPopis, setNewPopis] = useState("");
  /** Smer novej štruktúry — kým naň user nesiahne, ide podľa typu. */
  const [newSmer, setNewSmer] = useState<boolean | null>(null);
  // Výrobcovia dosiek z cenníka — formulár do KOLA 168 ponúkal napevno
  // päť značiek a Swiss Krono ani FunderMax sa vybrať nedali
  const vyrobcoviaQuery = useVyrobcovia({ sekcia: "materialy" }, { enabled: adding });

  const data = useMemo(() => query.data?.data ?? [], [query.data]);
  const labels = query.data?.typ_povrchu_labels ?? {};

  const vyrobcovia = [...new Set(data.map((d) => d.vyrobca))].sort();
  const typy = Object.keys(labels);
  const navrhyVyrobcov = useMemo(
    () =>
      [
        ...new Set([
          ...data.map((d) => d.vyrobca).filter((v) => v !== "Nezaradené"),
          ...(vyrobcoviaQuery.data?.data ?? []),
        ]),
      ].sort((a, b) => a.localeCompare(b, "sk")),
    [data, vyrobcoviaQuery.data],
  );
  const smerNovej = newSmer ?? TYPY_SO_SMEROM.has(newTyp);

  const filtered = data.filter((d) => {
    if (filterVyrobca !== "all" && d.vyrobca !== filterVyrobca) return false;
    if (filterTyp !== "all" && d.typ_povrchu !== filterTyp) return false;
    if (searchS) {
      const q = searchS.toLowerCase();
      if (
        !d.kod.toLowerCase().includes(q) &&
        !d.nazov.toLowerCase().includes(q) &&
        !d.popis.toLowerCase().includes(q)
      )
        return false;
    }
    return true;
  });

  function zrusPridanie(): void {
    setAdding(false);
    setNewKod("");
    setNewNazov("");
    setNewPopis("");
    setNewSmer(null);
  }

  function handleAdd(): void {
    if (!newKod.trim() || !newNazov.trim() || !newVyrobca.trim()) return;
    addMutation.mutate(
      {
        kod: newKod.trim(),
        nazov: newNazov.trim(),
        vyrobca: newVyrobca.trim(),
        typ_povrchu: newTyp,
        popis: newPopis.trim() || null,
        ma_smer: smerNovej,
      },
      {
        onSuccess: () => {
          toast({ title: "Štruktúra pridaná", description: newKod.trim() });
          zrusPridanie();
        },
        onError: (err) => {
          toast({
            variant: "destructive",
            title: "Chyba pri pridávaní štruktúry",
            description: extractErrorMessage(err),
          });
        },
      },
    );
  }

  function prepniSmer(item: StrukturaItem, maSmer: boolean): void {
    if (!item.id) return;
    patchMutation.mutate(
      { id: item.id, data: { ma_smer: maSmer } },
      {
        onSuccess: () => {
          toast({
            title: maSmer ? "Štruktúra so smerom vlákien" : "Štruktúra bez smeru vlákien",
            description: `${item.kod} (${item.vyrobca}) — dosky sa prepočítajú pri ďalšom importe cenníka.`,
          });
        },
        onError: (err) => {
          toast({
            variant: "destructive",
            title: "Smer vlákien sa nezmenil",
            description: extractErrorMessage(err),
          });
        },
      },
    );
  }

  function handleDelete(item: StrukturaItem): void {
    if (!item.id) return;
    if (!window.confirm(`Odstrániť štruktúru ${item.kod} (${item.vyrobca})?`)) return;
    deleteMutation.mutate(item.id, {
      onSuccess: () => {
        toast({ title: "Štruktúra odstránená", description: `${item.kod} (${item.vyrobca})` });
      },
      onError: (err) => {
        toast({
          variant: "destructive",
          title: "Chyba pri odstraňovaní štruktúry",
          description: extractErrorMessage(err),
        });
      },
    });
  }

  if (query.isLoading) {
    return (
      <div className="space-y-2">
        {Array.from({ length: 3 }).map((_, i) => (
          <Skeleton key={i} className="h-24 w-full" />
        ))}
      </div>
    );
  }

  if (query.isError) {
    return (
      <p className="text-sm text-destructive">
        Chyba pri načítaní štruktúr: {query.error?.message}
      </p>
    );
  }

  return (
    <div className="space-y-4">
      <p className="text-sm text-muted-foreground">
        Katalóg povrchových štruktúr podľa výrobcu — ten istý kód môžu mať dvaja
        výrobcovia s iným povrchom. Typ povrchu určuje charakter (lesk, mat, pór,
        synchro...) a používa sa pri automatickom výbere hrán. <b>Smer vlákien</b>:
        doska so štruktúrou, ktorá kreslí drevo, dostane pri importe cenníka smer X
        a pri porezaní sa neotáča. Zmena platí od ďalšieho importu.
      </p>

      <div className="flex flex-wrap gap-3 items-end">
        <div>
          <label
            htmlFor="struktury-vyrobca"
            className="text-xs font-semibold uppercase text-muted-foreground"
          >
            Výrobca
          </label>
          <select
            id="struktury-vyrobca"
            className="block border rounded px-2 py-1 text-sm"
            value={filterVyrobca}
            onChange={(e) => setFilterVyrobca(e.target.value)}
          >
            <option value="all">Všetci</option>
            {vyrobcovia.map((v) => (
              <option key={v} value={v}>
                {v}
              </option>
            ))}
          </select>
        </div>
        <div>
          <label
            htmlFor="struktury-typ"
            className="text-xs font-semibold uppercase text-muted-foreground"
          >
            Typ povrchu
          </label>
          <select
            id="struktury-typ"
            className="block border rounded px-2 py-1 text-sm"
            value={filterTyp}
            onChange={(e) => setFilterTyp(e.target.value)}
          >
            <option value="all">Všetky</option>
            {typy.map((t) => (
              <option key={t} value={t} title={labels[t]}>
                {kratkyTyp(labels, t)}
              </option>
            ))}
          </select>
        </div>
        <div className="flex-1">
          <input
            placeholder="Hľadať kód, názov..."
            className="border rounded px-2 py-1 text-sm w-full"
            value={searchS}
            onChange={(e) => setSearchS(e.target.value)}
          />
        </div>
        <button
          type="button"
          onClick={() => setAdding(true)}
          title="Pridať povrchovú štruktúru"
          className="px-3 py-1 bg-primary text-primary-foreground rounded text-sm font-medium"
        >
          + Nová štruktúra
        </button>
      </div>

      {adding && (
        <div className="rounded-lg border bg-card p-4 space-y-2">
          <div className="grid grid-cols-5 gap-2">
            <input
              placeholder="Kód (napr. XX)"
              aria-label="Kód novej štruktúry"
              value={newKod}
              onChange={(e) => setNewKod(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
            />
            <input
              placeholder="Názov"
              aria-label="Názov novej štruktúry"
              value={newNazov}
              onChange={(e) => setNewNazov(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
            />
            <input
              placeholder="Výrobca"
              aria-label="Výrobca novej štruktúry"
              value={newVyrobca}
              onChange={(e) => setNewVyrobca(e.target.value)}
              list="struktury-vyrobcovia"
              className="border rounded px-2 py-1 text-sm"
            />
            <datalist id="struktury-vyrobcovia">
              {navrhyVyrobcov.map((v) => (
                <option key={v} value={v} />
              ))}
            </datalist>
            <select
              aria-label="Typ povrchu novej štruktúry"
              value={newTyp}
              onChange={(e) => setNewTyp(e.target.value as TypPovrchu)}
              className="border rounded px-2 py-1 text-sm"
            >
              {typy.map((t) => (
                <option key={t} value={t} title={labels[t]}>
                  {kratkyTyp(labels, t)}
                </option>
              ))}
            </select>
            <input
              placeholder="Popis"
              aria-label="Popis novej štruktúry"
              value={newPopis}
              onChange={(e) => setNewPopis(e.target.value)}
              className="border rounded px-2 py-1 text-sm"
            />
          </div>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              aria-label="Smer vlákien novej štruktúry"
              checked={smerNovej}
              onChange={(e) => setNewSmer(e.target.checked)}
              className="h-3.5 w-3.5 accent-primary cursor-pointer"
            />
            Smer vlákien
            <span className="text-[11px] text-muted-foreground">
              — štruktúra kreslí drevo (pór a synchro áno, kým to nezmeníš)
            </span>
          </label>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={handleAdd}
              disabled={
                addMutation.isPending ||
                !newKod.trim() ||
                !newNazov.trim() ||
                !newVyrobca.trim()
              }
              className="px-3 py-1 bg-primary text-primary-foreground rounded text-sm disabled:opacity-50"
            >
              {addMutation.isPending ? "Pridáva sa…" : "Pridať"}
            </button>
            <button
              type="button"
              onClick={zrusPridanie}
              className="px-3 py-1 border rounded text-sm"
            >
              Zrušiť
            </button>
          </div>
        </div>
      )}

      <div className="text-xs text-muted-foreground">
        {filtered.length} z {data.length} štruktúr
      </div>

      <div className="rounded-lg border bg-card">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-xs text-muted-foreground border-b">
              <th className="px-3 py-2 text-left font-normal">Výrobca</th>
              <th className="px-3 py-2 text-left font-normal">Kód</th>
              <th className="px-3 py-2 text-left font-normal">Názov</th>
              <th className="px-3 py-2 text-left font-normal">Typ povrchu</th>
              <th className="px-3 py-2 text-center font-normal">Smer vlákien</th>
              <th className="px-3 py-2 text-right font-normal">Položiek</th>
              <th className="px-3 py-2" />
            </tr>
          </thead>
          <tbody>
            {filtered.map((item) => (
              <tr
                key={item.id ?? `${item.vyrobca}|${item.kod}`}
                className="border-b last:border-0 hover:bg-accent/50 group"
              >
                <td className="px-3 py-1.5 whitespace-nowrap">{item.vyrobca}</td>
                <td className="px-3 py-1.5 font-mono font-bold whitespace-nowrap">
                  {item.kod}
                </td>
                <td className="px-3 py-1.5">
                  <div className="font-medium">{item.nazov}</div>
                  {item.popis ? (
                    <div className="text-xs text-muted-foreground">{item.popis}</div>
                  ) : null}
                </td>
                <td className="px-3 py-1.5">
                  <span
                    className={`px-1.5 py-0 rounded text-[10px] font-medium ${TYP_COLORS[item.typ_povrchu] ?? "bg-gray-100"}`}
                    title={labels[item.typ_povrchu]}
                  >
                    {kratkyTyp(labels, item.typ_povrchu)}
                  </span>
                </td>
                <td className="px-3 py-1.5 text-center">
                  <input
                    type="checkbox"
                    aria-label={`Smer vlákien ${item.kod} ${item.vyrobca}`}
                    checked={!!item.ma_smer}
                    disabled={!item.id || patchMutation.isPending}
                    onChange={(e) => prepniSmer(item, e.target.checked)}
                    title={
                      item.id
                        ? "Dosky s touto štruktúrou dostanú pri importe smer vlákien X"
                        : "Kód nie je v katalógu — pridaj ho ako štruktúru výrobcu"
                    }
                    className="h-3.5 w-3.5 accent-primary cursor-pointer disabled:cursor-not-allowed"
                  />
                </td>
                <td className="px-3 py-1.5 text-right text-xs text-muted-foreground">
                  {item.pocet ? `${item.pocet}×` : null}
                </td>
                <td className="px-3 py-1.5 text-right">
                  {item.id ? (
                    <button
                      type="button"
                      onClick={() => handleDelete(item)}
                      disabled={deleteMutation.isPending}
                      className="opacity-0 group-hover:opacity-100 focus:opacity-100 text-destructive hover:bg-destructive/10 rounded p-1 text-xs transition-opacity disabled:opacity-30"
                      title="Odstrániť"
                      aria-label={`Odstrániť ${item.kod} ${item.vyrobca}`}
                    >
                      ✕
                    </button>
                  ) : null}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
